import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostBinding,
  inject,
  input,
  Input,
  InputSignal,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';

import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';

import {
  animate,
  state,
  style,
  transition,
  trigger,
  AnimationEvent,
} from '@angular/animations';

import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuService } from './app.menu.service';
import { LayoutService } from './service/app.layout.service';
import { DomHandler } from 'primeng/dom';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { AmplitudeService } from '../services/amplitude.service';
import { AppMenuItem } from './app.menu.component';

@Component({
  selector: '[app-menuitem]',
  standalone: true,
  imports: [CommonModule, TooltipModule, RouterLink, RouterLinkActive],
  templateUrl: './app.menuitem.component.html',
  styleUrls: ['./app.menuitem.component.scss'],
  animations: [
    trigger('children', [
      state(
        'collapsed',
        style({
          height: '0',
        }),
      ),
      state(
        'expanded',
        style({
          height: '*',
        }),
      ),
      state(
        'hidden',
        style({
          display: 'none',
        }),
      ),
      state(
        'visible',
        style({
          display: 'block',
        }),
      ),
      transition(
        'collapsed <=> expanded',
        animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'),
      ),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppMenuitemComponent implements OnInit, OnDestroy {
  public item: InputSignal<AppMenuItem> = input.required<AppMenuItem>();

  public index: InputSignal<number> = input.required<number>();

  @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;

  @Input() parentKey!: string;

  @ViewChild('submenu') submenu!: ElementRef;

  public active: WritableSignal<boolean> = signal(false);

  private menuSourceSubscription: Subscription;

  private menuResetSubscription: Subscription;

  private routerEventsSubscription: Subscription;

  public key: WritableSignal<string> = signal('');

  private amplitudeService = inject(AmplitudeService);

  constructor(
    public layoutService: LayoutService,
    public router: Router,
    private menuService: MenuService,
  ) {
    this.menuSourceSubscription = this.menuService.menuSource$.subscribe(
      (value) => {
        Promise.resolve(null).then(() => {
          if (value.routeEvent) {
            this.active.set(
              value.key === this.key() ||
                value.key.startsWith(this.key() + '-'),
            );
          } else {
            if (
              value.key !== this.key() &&
              !value.key.startsWith(this.key() + '-')
            ) {
              this.active.set(false);
            }
          }
        });
      },
    );

    this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
      this.active.set(false);
    });

    this.routerEventsSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isSlim() || this.isHorizontal() || this.isCompact()) {
          this.active.set(false);
        } else {
          if (this.item().routerLink) {
            this.updateActiveStateFromRoute();
          }
        }
      });
  }

  ngOnInit() {
    this.key.set(
      this.parentKey
        ? this.parentKey + '-' + this.index()
        : String(this.index()),
    );

    if (
      !(this.isSlim() || this.isHorizontal() || this.isCompact()) &&
      this.item().routerLink
    ) {
      this.updateActiveStateFromRoute();
    }
  }

  ngAfterViewChecked() {
    if (
      this.root &&
      this.active() &&
      this.layoutService.isDesktop() &&
      (this.layoutService.isHorizontal() ||
        this.layoutService.isSlim() ||
        this.layoutService.isCompact())
    ) {
      this.calculatePosition(
        this.submenu?.nativeElement,
        this.submenu?.nativeElement.parentElement,
      );
    }
  }

  updateActiveStateFromRoute() {
    let activeRoute = this.router.isActive(
      this.item().routerLink?.[0] ?? '',
      this.item().routerLinkActiveOptions || {
        paths: 'exact',
        queryParams: 'ignored',
        matrixParams: 'ignored',
        fragment: 'ignored',
      },
    );

    if (activeRoute) {
      this.menuService.onMenuStateChange({ key: this.key(), routeEvent: true });
    }
  }

  onSubmenuAnimated(event: AnimationEvent) {
    if (
      event.toState === 'visible' &&
      this.layoutService.isDesktop() &&
      (this.layoutService.isHorizontal() ||
        this.layoutService.isSlim() ||
        this.layoutService.isCompact())
    ) {
      const el = <HTMLUListElement>event.element;
      const elParent = <HTMLUListElement>el.parentElement;
      this.calculatePosition(el, elParent);
    }
  }

  calculatePosition(overlay: HTMLElement, target: HTMLElement) {
    if (overlay) {
      let { left, top } = target.getBoundingClientRect();
      const [vWidth, vHeight] = [window.innerWidth, window.innerHeight];
      const [oWidth, oHeight] = [overlay.offsetWidth, overlay.offsetHeight];
      const scrollbarWidth = DomHandler.calculateScrollbarWidth();

      // workaround to fix position, discounting the height of the top menu bar (84px)
      top -= 84;

      // reset
      overlay.style.top = '';
      overlay.style.left = '';

      if (this.layoutService.isHorizontal()) {
        const horizontalPosition = left + oWidth + scrollbarWidth;
        const leftPos =
          vWidth < horizontalPosition
            ? left - (horizontalPosition - vWidth)
            : left;
        overlay.style.left = `${leftPos}px`;
      } else if (
        this.layoutService.isSlim() ||
        this.layoutService.isCompact()
      ) {
        const verticalPosition = top + oHeight;
        const topPos =
          vHeight < verticalPosition ? top - (verticalPosition - vHeight) : top;
        overlay.style.top = `${topPos}px`;
      }
    }
  }

  itemClick(event: Event) {
    this.amplitudeService.trackEvent(
      `Menu Item Clicked: '${this.item().label}'`,
    );

    // avoid processing disabled items
    if (this.item().disabled) {
      event.preventDefault();
      return;
    }

    // navigate with hover
    if (
      (this.root && this.isSlim()) ||
      this.isHorizontal() ||
      this.isCompact()
    ) {
      this.layoutService.menuHoverActive.set(
        !this.layoutService.menuHoverActive(),
      );
    }

    // execute command
    const command = this.item().command;
    if (command) {
      command({ originalEvent: event, item: this.item() });
    }

    // toggle active state
    if (this.item().items) {
      this.active.set(!this.active());

      if (
        this.root &&
        this.active() &&
        (this.isSlim() || this.isHorizontal() || this.isCompact())
      ) {
        this.layoutService.onOverlaySubmenuOpen();
      }
    } else {
      if (this.layoutService.isMobile()) {
        this.layoutService.staticMenuMobileActive.set(false);
      }

      if (this.isSlim() || this.isHorizontal() || this.isCompact()) {
        this.menuService.reset();
        this.layoutService.menuHoverActive.set(false);
      }
    }

    this.menuService.onMenuStateChange({ key: this.key() });
  }

  onMouseEnter() {
    // activate item on hover
    if (
      this.root &&
      (this.isSlim() || this.isHorizontal() || this.isCompact()) &&
      this.layoutService.isDesktop()
    ) {
      this.active.set(true);
      this.menuService.onMenuStateChange({ key: this.key() });
    }
  }

  submenuAnimation = computed(() => {
    if (
      this.layoutService.isDesktop() &&
      (this.layoutService.isHorizontal() ||
        this.layoutService.isSlim() ||
        this.layoutService.isCompact())
    )
      return this.active() ? 'visible' : 'hidden';
    else
      return this.root ? 'expanded' : this.active() ? 'expanded' : 'collapsed';
  });

  isHorizontal = computed(() => this.layoutService.isHorizontal());

  isSlim = computed(() => this.layoutService.isSlim());

  isCompact = computed(() => this.layoutService.isCompact());

  @HostBinding('class.active-menuitem')
  get activeClass() {
    return this.active() && !this.root;
  }

  ngOnDestroy() {
    this.menuSourceSubscription?.unsubscribe();
    this.menuResetSubscription?.unsubscribe();
    this.routerEventsSubscription?.unsubscribe();
  }
}
